import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.serialization.Serializable

/**
 * Version of the game-doc schema/behavior this client speaks. Bump ONLY when a change makes games
 * created by this build unplayable on older builds (a new required field, changed turn semantics…) —
 * not on every release. It is stamped on every created game; joining a game stamped with a higher
 * version fails with an "update the app" message instead of misbehaving mid-game.
 *
 * Deploy order for a bump: Firestore rules first (they must allow whatever new schema motivated the
 * bump), then the app release. See docs/app-compat.md.
 */
const val GAME_PROTOCOL_VERSION = 3

fun isGameProtocolSupported(
    gameProtocolVersion: Int,
    clientProtocolVersion: Int = GAME_PROTOCOL_VERSION,
): Boolean = gameProtocolVersion <= clientProtocolVersion

/** Which store this build updates from — decides which minimum in [AppCompatConfig] applies. */
enum class UpdateStore { PLAY, APP_STORE, NONE }

data class ClientBuildInfo(val buildNumber: Int?, val store: UpdateStore)

/** This build's number and store. Web/desktop report no build number and are never gated. */
expect fun clientBuildInfo(): ClientBuildInfo

/**
 * Remote kill switch for incompatible builds, read from `appConfig/appCompat`. Raise a platform's
 * minimum to the lowest acceptable build number and older installs show a blocking update screen at
 * next launch. All fields optional; a missing doc gates nothing.
 */
@Serializable
internal data class AppCompatConfig(
    val minAndroidBuild: Int = 0,
    val minIosBuild: Int = 0,
    val androidUpdateUrl: String? = null,
    val iosUpdateUrl: String? = null,
    val updateMessage: String? = null,
)

data class UpdateRequirement(val message: String?, val storeUrl: String?)

internal fun evaluateUpdateRequirement(
    build: ClientBuildInfo,
    config: AppCompatConfig,
): UpdateRequirement? {
    val buildNumber = build.buildNumber ?: return null
    val (minBuild, storeUrl) = when (build.store) {
        UpdateStore.PLAY -> config.minAndroidBuild to (config.androidUpdateUrl ?: PLAY_STORE_URL)
        UpdateStore.APP_STORE -> config.minIosBuild to config.iosUpdateUrl
        UpdateStore.NONE -> return null
    }
    if (buildNumber >= minBuild) return null
    return UpdateRequirement(message = config.updateMessage, storeUrl = storeUrl)
}

object AppUpdateGate {
    private val _updateRequirement = MutableStateFlow<UpdateRequirement?>(null)
    val updateRequirement: StateFlow<UpdateRequirement?> = _updateRequirement.asStateFlow()

    /**
     * Reads `appConfig/appCompat` and raises the gate if this build is below its platform minimum.
     * Fails open on any error (offline, missing doc, rules) so a config hiccup can never lock
     * everyone out.
     */
    suspend fun refresh() {
        try {
            FirebaseAuthSession.requireUid()
            val snapshot = Firebase.firestore
                .collection(APP_CONFIG_COLLECTION)
                .document(APP_COMPAT_DOCUMENT)
                .get()
            if (!snapshot.exists) return
            val config = snapshot.data(AppCompatConfig.serializer())
            _updateRequirement.value = evaluateUpdateRequirement(clientBuildInfo(), config)
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            // Fail open: never block play because the compat check itself failed.
        }
    }
}

private const val APP_CONFIG_COLLECTION = "appConfig"
private const val APP_COMPAT_DOCUMENT = "appCompat"
private const val PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.bryankeltonadams.banko"
